<template>
  <!--
    Studio · Profile tab — public-facing card the way visitors see
    the reader on /reader/<subdomain>. Each editable element shows
    a pencil ✏ that opens the studio AI chat scoped to that field.
    Aida asks a static opener; the Mystic's free-form reply is
    polished by the BE and written straight into the form below the
    card (which still owns the actual ``updateReader`` save flow).

    Non-editable read-only display here: stats (#rank, readings,
    seekers, shared) — those are computed on the BE.
  -->
  <div class="studio-panel profile-panel profile-wide" ref="panelRoot">
    <!-- Every editor opens in the Conjure-style modal (opaque scrim + slide-in);
         the profile card stays mounted behind it. The assistant pane on the left
         stays visible (scrim starts at the studio content's left edge). -->
    <StudioEditModal :open="view === 'avatar'" title="Edit Avatar"
                     :right-offset="modalRight" :max-width="760" :align="huggingChat ? 'start' : 'center'" @close="onAvatarPickerClose">
      <AvatarPicker embedded @close="onAvatarPickerClose" />
    </StudioEditModal>

    <StudioEditModal :open="view === 'drop'" title="Daily Affirmation"
                     :right-offset="modalRight" :max-width="760" :align="huggingChat ? 'start' : 'center'" @close="onDropClose">
      <SyndicatePanel />
    </StudioEditModal>

    <!-- Text field (name / teaser / description) — Aida's draft lands here while
         she helps from the side panel. Mirrors the assistant's active field. -->
    <StudioEditModal :open="!!textModalField" :title="textModalTitle"
                     :right-offset="modalRight" :align="huggingChat ? 'start' : 'center'" @close="closeTextModal">
      <div class="text-modal-body">
        <p class="text-modal-current" v-if="textModalValue">{{ textModalValue }}</p>
        <p class="text-modal-empty" v-else>Nothing here yet.</p>

        <div v-if="textModalField === 'teaser' && teaserDraft !== null" class="draft-card">
          <div class="draft-label">Proposed</div>
          <p class="draft-text">{{ teaserDraft || '(empty)' }}</p>
          <div class="draft-actions">
            <button class="ghost-btn-sm" :disabled="draftSaving" @click="discardTeaser">Discard</button>
            <button class="primary-btn-sm" :disabled="draftSaving || !teaserDraft.trim()" @click="publishTeaser">{{ draftSaving ? 'Publishing…' : 'Publish' }}</button>
          </div>
        </div>
        <div v-else-if="textModalField === 'reader_description' && descriptionDraft !== null" class="draft-card">
          <div class="draft-label">Proposed</div>
          <p class="draft-text">{{ descriptionDraft || '(empty)' }}</p>
          <div class="draft-actions">
            <button class="ghost-btn-sm" :disabled="draftSaving" @click="discardDescription">Discard</button>
            <button class="primary-btn-sm" :disabled="draftSaving || !descriptionDraft.trim()" @click="publishDescription">{{ draftSaving ? 'Publishing…' : 'Publish' }}</button>
          </div>
        </div>
        <p v-if="fieldErrors[textModalField]" class="field-err" role="alert">{{ fieldErrors[textModalField] }}</p>

        <p class="text-modal-hint">Mystic AIda is in the side panel - tell her what you want and her draft appears here.</p>
      </div>
    </StudioEditModal>

    <!-- Hero — avatar left; name, the Mystic's own signs, and its birth in one
         column beside it. The birth is a fact once it was cast at conception:
         a cake with no pencil, and "Read my chart" beside it. -->
    <div class="profile-hero">
      <div class="profile-avatar-wrap">
        <img
          v-if="form.background_image"
          :src="form.background_image"
          :alt="form.name + ' avatar'"
          class="profile-avatar"
        />
        <div v-else class="profile-avatar-placeholder">✦</div>
        <button
          type="button"
          class="mystic-pencil avatar-pencil"
          title="Change avatar"
          aria-label="Change avatar"
          @click="view = 'avatar'"
        ><i class="fas fa-pencil"></i></button>
      </div>
      <div class="profile-hero-meta">
        <div class="identity-name-row">
          <h1 class="profile-name one-line" :title="form.name">{{ form.name || 'Your reader needs a name' }}</h1>
          <FieldHelpIcon
            field="name" label="Name" panel="profile"
            :value="form.name" variant="pencil"
            title="Help me write the reader name with Mystic Aida"
          />
        </div>
          <p v-if="fieldErrors.name" class="field-err" role="alert" data-field-error="name">{{ fieldErrors.name }}</p>

        <!-- Sun + Moon + Rising — THIS Mystic's, below its name. The pencil
             paints a custom sign image; it never changes the sign. -->
        <div v-if="signList.length" class="identity-signs">
          <div v-for="s in signList" :key="s.which" class="identity-sign-card">
            <img v-if="s.img" :src="s.img" class="identity-sign-img" :alt="`${s.value} ${s.role}`" />
            <span v-else class="identity-sign-glyph">{{ glyph(s.value) }}</span>
            <div class="identity-sign-text">
              <span class="identity-sign-name">{{ s.value }}</span>
              <span class="identity-sign-role">{{ s.role }}</span>
            </div>
            <button
              type="button"
              class="mystic-pencil identity-sign-edit"
              :title="`Create a custom ${s.value} ${s.role} image`"
              :aria-label="`Edit ${s.role} sign`"
              @click="editSign(s.which, s.value)"
            ><i class="fas fa-pencil"></i></button>
          </div>
        </div>

        <!-- The birth. Fixed (cast at conception): a fact, no pencil. Legacy
             Mystics with no cast birth keep the editor so they can be given one. -->
        <div class="profile-born-row">
          <span v-if="birthFixed" class="profile-born fixed"
                :title="`Born ${bornLabel}. This never changes.`">
            <span class="profile-born-label">{{ bornLabel }}</span>
          </span>
          <button v-else class="profile-born" @click="openBirth"
                  :title="form.birth_date ? 'Edit birth details' : 'Add birth details'">
            <span class="profile-born-label">{{ form.birth_date ? 'Birth details' : 'Add birth details' }}</span>
            <i class="fas fa-pencil profile-born-edit"></i>
          </button>
          <button v-if="birthFixed" type="button" class="profile-read-chart" data-read-chart
                  @click="openChart">Read my chart</button>
        </div>
      </div>
    </div>

    <!-- "Read my chart" — the same sheet the seeker read at the birth. -->
    <div v-if="chartOpen" class="chart-modal" role="dialog" aria-modal="true" aria-label="My chart"
         @click.self="chartOpen = false">
      <div class="chart-modal-card">
        <button type="button" class="chart-modal-close" aria-label="Close" @click="chartOpen = false">×</button>
        <p v-if="chartLoading" class="chart-modal-wait">Reading the sky…</p>
        <p v-else-if="chartError" class="chart-modal-wait">{{ chartError }}</p>
        <ChartSheet v-else-if="chartSheet" :chart="chartSheet" />
      </div>
    </div>

    <!-- Birth-details editor — the same calendar + map the seeker Mirror uses. -->
    <BirthDetailsModal
      v-if="birthModalOpen"
      :birthdate="birthForm.birthdate"
      :birthtime="birthForm.birthtime"
      :birthplace="birthForm.birthplace"
      :saving="savingBirth"
      :error="birthError"
      :explain="false"
      @save="saveBirth"
      @close="birthModalOpen = false"
    />

    <!-- Profile sub-tabs — Identity / Voice / Doorman, below the hero. -->
    <nav class="profile-subtabs">
      <button
        v-for="t in PROFILE_TABS" :key="t.key"
        type="button" class="profile-subtab"
        :class="{ active: profileTab === t.key }"
        @click="profileTab = t.key"
      >{{ t.label }}</button>
    </nav>

    <div v-show="profileTab === 'identity'">
    <h2 class="profile-section-desc">How seekers see you</h2>

    <div v-if="loading" class="profile-loading">Loading your profile…</div>

    <div v-else class="profile-card">
      <!-- Teaser at the top. Aida's polished draft stages in ``teaserDraft``
           (studio-assistant:field-staged) with Publish / Discard inline. -->
      <div class="profile-block">
        <div class="block-head">
          <span class="block-label">Teaser</span>
          <FieldHelpIcon
            field="teaser" label="Teaser" panel="profile"
            :value="form.teaser" variant="pencil"
            title="Help me write the teaser with Mystic Aida"
          />
        </div>
        <p class="profile-teaser" v-if="form.teaser">
          {{ form.teaser }}
        </p>
        <p class="profile-empty" v-else>
          A short tagline goes here - one or two sentences seekers
          read first.
        </p>

        <div v-if="teaserDraft !== null" class="draft-card">
          <div class="draft-label">Proposed</div>
          <p class="draft-text">{{ teaserDraft || '(empty)' }}</p>
          <div class="draft-actions">
            <button
              class="ghost-btn-sm"
              :disabled="draftSaving"
              @click="discardTeaser"
            >Discard</button>
            <button
              class="primary-btn-sm"
              :disabled="draftSaving || !teaserDraft.trim()"
              @click="publishTeaser"
            >{{ draftSaving ? 'Publishing…' : 'Publish' }}</button>
          </div>
        </div>
        <p v-if="fieldErrors.teaser" class="field-err" role="alert" data-field-error="teaser">{{ fieldErrors.teaser }}</p>
      </div>

      <!-- Daily drop hero (eyebrow + image + caption + pencil → affirmation/settings). -->
      <div class="identity-drop">
        <!-- Eyebrow — drop name + date, above the image (matches the community card). -->
        <div v-if="dropName || dropDate" class="identity-drop-over">
          <span class="identity-drop-over-name">{{ dropName }}</span>
          <span v-if="dropName && dropDate" class="identity-drop-over-dot">·</span>
          <span v-if="dropDate" class="identity-drop-over-date">{{ dropDate }}</span>
        </div>
        <div class="identity-drop-hero">
          <img
            v-if="dropImage"
            :src="dropImage"
            class="identity-drop-img"
            alt="Today's daily drop"
          />
          <div v-else class="identity-drop-noimg">✦</div>
          <!-- Edit pencil — top-right of the image. -->
          <button
            type="button"
            class="mystic-pencil identity-drop-edit"
            title="Edit daily affirmation & settings"
            aria-label="Edit daily affirmation and settings"
            @click="view = 'drop'"
          ><i class="fas fa-pencil"></i></button>
          <!-- Caption strip — the affirmation over a dark gradient. -->
          <div v-if="dropCaption" class="identity-drop-footer">
            <p class="identity-drop-caption">{{ dropCaption }}</p>
          </div>
        </div>
      </div>

      <!-- Description. -->
      <div class="profile-block">
        <div class="block-head">
          <span class="block-label">Description</span>
          <FieldHelpIcon
            field="reader_description" label="Description" panel="profile"
            :value="form.reader_description" variant="pencil"
            title="Help me write the description with Mystic Aida"
          />
        </div>
        <p class="profile-description" v-if="form.reader_description">
          {{ form.reader_description }}
        </p>
        <p class="profile-empty" v-else>
          A longer paragraph that introduces your reader's voice, the
          kind of guidance they offer, and who they're for.
        </p>

        <div v-if="descriptionDraft !== null" class="draft-card">
          <div class="draft-label">Proposed</div>
          <p class="draft-text">{{ descriptionDraft || '(empty)' }}</p>
          <div class="draft-actions">
            <button
              class="ghost-btn-sm"
              :disabled="draftSaving"
              @click="discardDescription"
            >Discard</button>
            <button
              class="primary-btn-sm"
              :disabled="draftSaving || !descriptionDraft.trim()"
              @click="publishDescription"
            >{{ draftSaving ? 'Publishing…' : 'Publish' }}</button>
          </div>
        </div>
        <p v-if="fieldErrors.reader_description" class="field-err" role="alert" data-field-error="reader_description">{{ fieldErrors.reader_description }}</p>
      </div>

      <!-- The back of your card (mystic room 2026-10-30): a seeker taps the
           portrait in your room and it turns over to these, a few words
           each. Left empty, the row does not show. -->
      <div class="card-back-edit">
        <p class="card-back-head">The back of your card</p>
        <label class="author-field">
          <span>Reads for</span>
          <input
            v-model="cardBack.reads_for"
            class="author-input" type="text" maxlength="60"
            data-testid="card-back-reads-for"
            placeholder="Who and what you read for, in a few words"
          />
        </label>
        <p v-if="fieldErrors.reads_for" class="field-err" role="alert" data-field-error="reads_for">{{ fieldErrors.reads_for }}</p>
        <label class="author-field">
          <span>Voice</span>
          <input
            v-model="cardBack.voice_short"
            class="author-input" type="text" maxlength="60"
            data-testid="card-back-voice"
            placeholder="How you sound, in two to four words"
          />
        </label>
        <p v-if="fieldErrors.voice_short" class="field-err" role="alert" data-field-error="voice_short">{{ fieldErrors.voice_short }}</p>
        <div class="author-edit-actions">
          <button
            type="button" class="primary-btn-sm" data-testid="card-back-save"
            :disabled="cardBackSaving" @click="saveCardBack"
          >{{ cardBackSaving ? 'Saving…' : 'Save' }}</button>
        </div>
      </div>

      <!-- Footer credits — "Conjured By: <author>". Author defaults
           to the reader's own name when authored_by is empty (the
           reader is conjured by themselves). When authored_by_website
           is set the author name renders as a hyperlink. The pencil
           opens an inline two-field editor (name + website) — no
           chat, no Aida. -->
      <div class="profile-footer">
        <span v-if="!editingAuthor" class="footer-by">
          Conjured By:
          <a
            v-if="form.authored_by_website"
            :href="form.authored_by_website"
            target="_blank"
            rel="noopener"
            class="author-link"
          >{{ effectiveAuthor }}</a>
          <span v-else>{{ effectiveAuthor }}</span>
          <button
            type="button"
            class="author-edit-btn"
            title="Edit author credit"
            aria-label="Edit author credit"
            @click="openAuthorEdit"
          >✎</button>
        </span>

        <div v-else class="author-edit-form">
          <label class="author-field">
            <span>Authored by</span>
            <input
              v-model="authorEdit.name"
              class="author-input"
              type="text"
              :placeholder="form.name || 'Your name or handle'"
            />
          </label>
          <label class="author-field">
            <span>Website (optional)</span>
            <input
              v-model="authorEdit.website"
              class="author-input"
              type="url"
              placeholder="https://your-site.com"
            />
            <small>If set, the name above becomes a link to this URL.</small>
          </label>
          <p v-if="fieldErrors.authored_by" class="field-err" role="alert" data-field-error="authored_by">{{ fieldErrors.authored_by }}</p>
          <div class="author-edit-actions">
            <button
              type="button"
              class="ghost-btn"
              :disabled="authorSaving"
              @click="cancelAuthorEdit"
            >Cancel</button>
            <button
              type="button"
              class="primary-btn"
              :disabled="authorSaving"
              @click="saveAuthorEdit"
            >{{ authorSaving ? 'Saving…' : 'Save' }}</button>
          </div>
        </div>
      </div>

      <!--
        Privacy lives inside the Profile card now — it controls
        public visibility, which is conceptually "how seekers see
        you" (or whether they see you at all).
      -->
      <div class="profile-privacy-row">
        <label class="profile-privacy-label">
          <input
            type="checkbox"
            :checked="form.not_listed_in_community"
            @change="onPrivacyToggle($event.target.checked)"
          />
          <span>Not listed in community</span>
        </label>
        <span class="profile-privacy-hint">Hide this reader from the public marketplace.</span>
        <p v-if="privacyError" class="save-status error">{{ privacyError }}</p>
      </div>

      <p v-if="saveStatus" class="save-status" :class="{ error: saveError }">
        {{ saveStatus }}
      </p>
    </div>

    </div><!-- /Identity tab -->

    <!-- Voice tab — "How seekers hear you": voice + the Mysticism dial. -->
    <section class="voice-card" v-show="profileTab === 'voice'">
      <h2 class="profile-section-desc">How seekers hear you</h2>
      <div class="voice-card-body">
        <p class="voice-card-intro">
          Click the pencil next to any section to ask Aida for help
          shaping it. Aida proposes the wording; you publish or
          discard each one inline.
        </p>
        <VoiceEditor
          v-model="voiceSections"
          @publish-section="onPublishVoiceSection"
        />
        <span v-if="voiceStatus" class="save-status" :class="{ error: voiceError }">
          {{ voiceStatus }}
        </span>

        <!-- Mysticism — reader-wide voice-intensity dial, part of Voice.
             Colors reveals, the reading, images, and drops. -->
        <div class="mysticism-block">
          <h3 class="mysticism-subhead">
            Mysticism
            <span class="mysticism-subhead-note"> - How mystical your words run</span>
          </h3>
          <input
            type="range" min="0" max="2" step="1"
            class="mysticism-slider"
            :value="mysticismIndex"
            @input="onMysticismSlide($event.target.value)"
            aria-label="Mysticism level"
          />
          <div class="mysticism-ticks">
            <span
              v-for="(opt, i) in MYSTICISM_OPTIONS" :key="opt.value"
              class="mysticism-tick" :class="{ active: mysticismIndex === i }"
            >{{ opt.label }}</span>
          </div>
          <p class="mysticism-hint">{{ MYSTICISM_OPTIONS[mysticismIndex].hint }}</p>
          <span v-if="mysticismStatus" class="save-status" :class="{ error: mysticismError }">{{ mysticismStatus }}</span>
        </div>
      </div>
    </section>

    <!-- Doorman tab — "How seekers arrive": the per-mode entry menu (button
         label + intro copy) the seeker sees on entry, with a live preview. -->
    <section class="doorman-card" v-show="profileTab === 'doorman'">
      <h2 class="profile-section-desc">How seekers arrive</h2>
      <div class="doorman-split" :class="{ 'doorman-split-open': showDoormanPreview }">
       <div class="doorman-col">
      <div class="doorman-card-body">
        <!-- Entry greeting — the welcome line shown above the buttons. -->
        <label class="doorman-field doorman-greeting-field">
          <span class="doorman-field-label">
            Greeting
            <FieldHelpIcon
              field="doorman:greeting"
              label="Doorman greeting"
              panel="doorman"
              :value="doormanGreeting"
            />
          </span>
          <textarea
            class="doorman-input doorman-textarea"
            rows="2"
            placeholder="e.g., Welcome. I'm here when you're ready - what would you like to do?"
            :value="doormanGreeting"
            @input="setGreeting($event.target.value)"
            @blur="saveDoorman"
          ></textarea>
        </label>

        <!-- Editable buttons — one per active skill (Get a … Reading) + Education. -->
        <div
          v-for="mode in editableModes"
          :key="mode.key"
          class="doorman-mode-row"
          :class="{ 'doorman-mode-hidden': mode.hidden }"
        >
          <div class="doorman-mode-header">
            <span class="doorman-mode-key">{{ mode.defaultLabel }}</span>
            <label class="doorman-show-toggle">
              <input
                type="checkbox"
                :checked="!mode.hidden"
                @change="setModeHidden(mode.key, !$event.target.checked)"
              />
              {{ mode.hidden ? 'Hidden' : 'Shown' }}
            </label>
          </div>
          <label class="doorman-field">
            <span class="doorman-field-label">
              Button label
              <FieldHelpIcon
                :field="`doorman:${mode.key}:label`"
                :label="`${mode.defaultLabel} button`"
                panel="doorman"
                :value="mode.labelOverride"
              />
            </span>
            <input
              type="text"
              class="doorman-input"
              :placeholder="mode.defaultLabel"
              :value="mode.labelOverride"
              @input="setModeLabel(mode.key, $event.target.value)"
              @blur="saveDoorman"
            />
          </label>
          <!-- Only Education speaks an intro; skill buttons just start the reading. -->
          <label v-if="mode.kind !== 'skill'" class="doorman-field">
            <span class="doorman-field-label">
              Intro text (streamed when tapped)
              <FieldHelpIcon
                :field="`doorman:${mode.key}:intro`"
                :label="`${mode.defaultLabel} intro`"
                panel="doorman"
                :value="mode.introOverride"
              />
            </span>
            <textarea
              class="doorman-input doorman-textarea"
              rows="2"
              placeholder="e.g., I'm ready when you are."
              :value="mode.introOverride"
              @input="setModeIntro(mode.key, $event.target.value)"
              @blur="saveDoorman"
            ></textarea>
          </label>
        </div>

        <div class="doorman-actions">
          <span
            v-if="doormanStatus"
            class="save-status"
            :class="{ error: doormanError }"
          >
            {{ doormanStatus }}
          </span>
          <button
            type="button"
            class="doorman-sim-btn"
            @click="showDoormanPreview = true"
          >
            ▶ Simulate
          </button>
        </div>
      </div>
       </div>

        <DoormanPreview
          v-if="showDoormanPreview"
          class="doorman-rail"
          :modes="previewModes"
          :greeting="doormanGreeting"
          :avatar-url="form.background_image"
          :reader-name="form.name || 'Your reader'"
          @close="showDoormanPreview = false"
        />
      </div>
    </section>
  </div>
</template>

<script setup>
import { reactive, ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import axios from 'axios'
import { store, static_store } from '@/store'
import { updateReader, updateMysticism, getMyReader, getMyChart } from '@/api-service'
import ChartSheet from '@/components/become-mystic/ChartSheet.vue'
import { parseAxiosError, clientToday } from '@/utils/general-utils'
import { profileFieldRejection } from '@/utils/profile-safety'
import FieldHelpIcon from '../FieldHelpIcon.vue'
import AvatarPicker from '../AvatarPicker.vue'
import SyndicatePanel from './SyndicatePanel.vue'
import VoiceEditor from '../VoiceEditor.vue'
import DoormanPreview from './DoormanPreview.vue'
import BirthDetailsModal from '@/components/widgets/BirthDetailsModal.vue'
import StudioEditModal from '../StudioEditModal.vue'
import { coerceVoiceSections } from '@/utils/voice-sections'
import { studioAssistantState, dispatchFieldContext } from '@/utils/studio-assistant'
import { buildSignList } from '@/utils/chart-signs'

const loading = ref(true)
const saveStatus = ref('')
const saveError = ref(false)

// ─── Edit modals (Conjure-style: opaque scrim + slide-in) ───────────────────
// Every editable element opens in a StudioEditModal. The scrim starts at the
// studio content's left edge (measured) so the Mystic AIda assistant pane stays
// visible on the left, exactly like the Community Conjure modal.
const panelRoot = ref(null)
const modalRight = ref(0)
const huggingChat = ref(false)
function measureModalRight() {
  // The chat floats on the RIGHT; anchor the scrim's right edge to the chat's LEFT
  // edge so the card hugs it (+10px gap). Fall back to 0 (full width, centered)
  // when the chat isn't open.
  const chat = document.querySelector('.ai-zone')
  if (chat) {
    const r = chat.getBoundingClientRect()
    if (r.width > 0 && r.left > 0) { modalRight.value = Math.round(window.innerWidth - r.left); huggingChat.value = true; return }
  }
  modalRight.value = 0
  huggingChat.value = false
}

// The field currently scoped in the side-assistant (drives the TEXT-field modal).
const editingField = computed(() =>
  studioAssistantState.open ? (studioAssistantState.activeField?.field || '') : ''
)
// Seeker-facing text fields each open a focused review modal (Aida's draft lands
// here). name has no draft flow — its modal is a short prompt to use the assistant.
const TEXT_MODAL_FIELDS = ['name', 'teaser', 'reader_description']
const textModalField = computed(() =>
  TEXT_MODAL_FIELDS.includes(editingField.value) ? editingField.value : ''
)
const TEXT_MODAL_TITLES = { name: 'Name', teaser: 'Teaser', reader_description: 'Description' }
const textModalTitle = computed(() => TEXT_MODAL_TITLES[textModalField.value] || 'Edit')
const textModalValue = computed(() => (textModalField.value ? (form[textModalField.value] || '') : ''))
// Closing the modal re-dispatches the active field as a click → the assistant's
// same-field toggle closes the panel, which clears editingField → modal closes.
function closeTextModal() {
  const f = editingField.value
  if (f) dispatchFieldContext({ field: f, label: '', panel: 'profile', value: '', source: 'click' })
}
// 'card' = profile card view; 'avatar' = full-panel takeover by the
// AvatarPicker. Pencil button on the avatar flips into 'avatar';
// the picker emits ``close`` to flip back. 'drop' opens the Daily
// Affirmation / Pro Settings tabs (SyndicatePanel) as a takeover —
// same pattern as the avatar pencil.
const view = ref('card')

// Today's daily-drop (image + caption + date) shown in the right column of the
// Identity card; its pencil opens the Daily Affirmation / Pro Settings takeover.
const dropImage = ref('')
const dropCaption = ref('')
const dropDate = ref('')
const dropName = ref('')
async function fetchDropImage() {
  try {
    const token = localStorage.getItem('authToken')
    const cfg = { params: { date: clientToday() } }
    if (token) cfg.headers = { Authorization: `Token ${token}` }
    const res = await axios.get(`${static_store.apiUrl}/reader/daily-affirmation/`, cfg)
    const d = res.data || {}
    dropImage.value = d.preview_image_url || d.image_url || ''
    dropCaption.value = d.message || ''
    dropName.value = d.drop_name || ''
    dropDate.value = d.drop_date
      ? new Date(d.drop_date + 'T00:00:00').toLocaleDateString('en-US',
          { weekday: 'long', month: 'short', day: 'numeric' })
      : ''
  } catch (e) { /* no drop yet — show placeholder */ }
}
function onDropClose() {
  view.value = 'card'
  fetchDropImage()   // reflect any new image painted in the takeover
}

// The reader's own sun + moon + rising (its chart, computed server-side from the
// reader's birth date/time/location) — shown as cards in the Identity left column.
// The Mystic's brand is theirs to shape, so they get the same big three the seeker
// sees on their Mirror, not a shorter version of it. Moon and Rising need a birth
// time and place; a Mystic who hasn't given those just sees their Sun.
const ZODIAC_GLYPHS = {
  Aries: '♈', Taurus: '♉', Gemini: '♊', Cancer: '♋', Leo: '♌', Virgo: '♍',
  Libra: '♎', Scorpio: '♏', Sagittarius: '♐', Capricorn: '♑', Aquarius: '♒', Pisces: '♓',
}
function glyph(s) { return ZODIAC_GLYPHS[s] || '✦' }
const chart = reactive({
  sun: null, moon: null, rising: null,
  sun_image: null, moon_image: null, rising_image: null,
})
const signList = computed(() => buildSignList(chart, {
  sun: chart.sun_image, moon: chart.moon_image, rising: chart.rising_image,
}))
// The birth, as my-reader reports it: fixed when it was cast at conception.
const mysticBirth = reactive({ fixed: false, date: null, time: null, place: '' })
const birthFixed = computed(() => !!mysticBirth.fixed)
const bornLabel = computed(() => {
  if (!mysticBirth.date) return ''
  const d = new Date(`${mysticBirth.date}T${(mysticBirth.time || '00:00:00')}`)
  const day = isNaN(d.getTime()) ? mysticBirth.date
    : d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })
  const time = mysticBirth.time && !isNaN(d.getTime())
    ? d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }) : ''
  return [day, time, mysticBirth.place].filter(Boolean).join(' · ')
})

// "Read my chart" — fetched on open, not on mount; most visits never open it.
const chartOpen = ref(false)
const chartLoading = ref(false)
const chartError = ref('')
const chartSheet = ref(null)
async function openChart() {
  chartOpen.value = true
  if (chartSheet.value) return
  chartLoading.value = true
  chartError.value = ''
  try {
    const r = await getMyChart()
    const data = (r && r.data) || r || {}
    if (data.ok === false) throw new Error(data.reason || 'no chart')
    chartSheet.value = data
  } catch (e) {
    chartError.value = 'My chart could not be read just now. Try again in a moment.'
  } finally {
    chartLoading.value = false
  }
}

async function fetchChart() {
  try {
    const r = await getMyReader()
    const data = (r && r.data) || r || {}
    if (data.mystic_birth) Object.assign(mysticBirth, data.mystic_birth)
    if (data.chart) {
      chart.sun = data.chart.sun
      chart.moon = data.chart.moon
      chart.rising = data.chart.rising
      chart.sun_image = data.chart.sun_image || null
      chart.moon_image = data.chart.moon_image || null
      chart.rising_image = data.chart.rising_image || null
    }
  } catch (e) { /* no chart — cards just don't render */ }
}
// The sign-image builder (in StudioContent) emits this when the Mystic paints
// or selects a brand sign image — update the card live without a refetch.
function onSignImageUpdated(e) {
  const d = e.detail || {}
  if (d.subject === 'reader_sun_sign') chart.sun_image = d.url
  else if (d.subject === 'reader_moon_sign') chart.moon_image = d.url
  else if (d.subject === 'reader_rising_sign') chart.rising_image = d.url
}
// Sign-card pencil — opens the Mystic AIda sign-image builder, same wiring as the
// Altar Mirror's editSign.
function editSign(which, value) {
  window.dispatchEvent(new CustomEvent('open-sign-builder', { detail: { which, sign: value, scope: 'reader' } }))
}

// Birth-details editor — same calendar + map the seeker Mirror uses (shared
// BirthDetailsModal). Saves birth-only via updateReader (a partial PATCH — never
// touches the reader's name/email), then refreshes the chart so the Sun/Rising
// cards recompute from the new details.
const birthModalOpen = ref(false)
const savingBirth = ref(false)
const birthError = ref('')
const birthForm = reactive({ birthdate: '', birthtime: '', birthplace: null })
function openBirth() {
  let bd = form.birth_date || ''
  birthForm.birthdate = bd.includes('T') ? bd.split('T')[0] : bd
  birthForm.birthtime = (form.birth_time || '').slice(0, 5)
  const lat = form.birth_latitude, lng = form.birth_longitude
  birthForm.birthplace = (lat != null && lng != null) ? { latitude: Number(lat), longitude: Number(lng) } : null
  birthModalOpen.value = true
}
async function saveBirth({ birthdate, birthtime, birthplace }) {
  if (savingBirth.value) return
  savingBirth.value = true
  birthError.value = ''
  try {
    const payload = {
      birth_date: birthdate || null,
      birth_time: birthtime || null,
      birth_latitude: birthplace ? birthplace.latitude : null,
      birth_longitude: birthplace ? birthplace.longitude : null,
    }
    await updateReader(payload)
    Object.assign(form, payload)
    if (store.reader) Object.assign(store.reader, payload)
    birthModalOpen.value = false
    await fetchChart()   // recompute Sun/Rising from the new birth details
  } catch (e) {
    birthError.value = parseAxiosError(e) || 'Could not save your birth details. Try again.'
  } finally {
    savingBirth.value = false
  }
}

// Staged drafts for fields that use the propose-then-publish flow
// (teaser today, description next). null = no staged draft. The
// chat panel writes here via ``studio-assistant:field-staged``;
// the operator hits Publish / Refine / Discard inline.
const teaserDraft = ref(null)
const descriptionDraft = ref(null)
const draftSaving = ref(false)

// Field-key → local draft ref. Used by the staged-event listener
// to route polish results into the matching slot without a long
// switch statement.
const STAGE_DRAFTS = {
  teaser: teaserDraft,
  reader_description: descriptionDraft,
}

function onAvatarPickerClose() {
  // Re-hydrate the form from store.reader so the avatar URL the
  // picker just wrote propagates into our card render.
  hydrate()
  view.value = 'card'
}

const form = reactive({
  name: '',
  teaser: '',
  reader_description: '',
  authored_by: '',
  authored_by_website: '',
  sub_domain: '',
  background_image: '',
  // Phase 3' — folded in from the deleted Identity tab. Privacy
  // toggle saves immediately; Voice has its own Save button below.
  not_listed_in_community: false,
  // Doorman v1 — per-mode menu entries. Editable: button_label /
  // intro_text (custom modes) and button_label_override /
  // intro_text_override (skill modes). The Mystic edits the
  // list inline and saves the whole thing at once.
  doorman_modes: [],
})

// Profile sub-tabs — Identity (how seekers see you), Voice (hear you),
// Doorman (arrive). v-show keeps all three mounted so form state survives
// switching tabs.
const PROFILE_TABS = [
  { key: 'identity', label: 'Identity' },
  { key: 'voice', label: 'Voice' },
  { key: 'doorman', label: 'Doorman' },
]
const profileTab = ref('identity')

// Sub-tab is deep-linkable: the parent (StudioContent) drives it via switchSub
// (for ?studio=…), and we report user clicks back up so the URL stays in sync.
const emit = defineEmits(['sub-changed'])
function switchSub(key) {
  if (PROFILE_TABS.some(t => t.key === key)) profileTab.value = key
}
watch(profileTab, (k) => emit('sub-changed', k))
defineExpose({ switchSub })

// Doorman card save state.
const doormanStatus = ref('')
const doormanError = ref(false)
const doormanSaving = ref(false)

const showDoormanPreview = ref(false)

// The editable Doorman buttons, keyed (not index-bound), so the auto-injected
// skill buttons ("Get a … Reading") are surfaced for configuration even though
// they aren't stored in doorman_modes — the live menu auto-discovers them from
// active skills (see doorman.compose_menu). One row per active skill, then
// Continue (returning seekers only) + Education.
function _skillDefaultLabel(skillKey) {
  return `Get a ${skillKey.charAt(0).toUpperCase()}${skillKey.slice(1)} Reading`
}
const editableModes = computed(() => {
  const stored = form.doorman_modes || []
  const byKey = Object.fromEntries(stored.map(m => [m.key, m]))
  // ACTIVE skill variants only — the same source the live Doorman uses. NOT
  // store.reader.skills (which counts any drop_group as astrology).
  const skills = (store.reader && store.reader.active_skill_keys) || []
  const rows = []
  // One row per active skill — tapping deducts emeralds and starts the reading
  // (Grounding); nothing is streamed, so no intro field.
  for (const sk of skills) {
    const key = `skill:${sk}`
    const e = byKey[key] || {}
    rows.push({
      key, kind: 'skill',
      defaultLabel: _skillDefaultLabel(sk),
      labelOverride: e.button_label_override || '',
      hidden: !!e.hidden,
    })
  }
  // Education — the only button that speaks an intro (its headline).
  const edu = byKey['education'] || {}
  rows.push({
    key: 'education', kind: 'education',
    defaultLabel: 'Education',
    labelOverride: edu.button_label || '', introOverride: edu.intro_text || '',
    hidden: !!edu.hidden,
  })
  // Continue is intentionally NOT shown — it's runtime-only (returning seeker),
  // not operator-configurable.
  return rows
})

// The Doorman entry greeting (shown above the buttons). Stored as a special
// non-button entry in doorman_modes (key='greeting').
const doormanGreeting = computed(() => {
  const g = (form.doorman_modes || []).find(m => m.key === 'greeting')
  return (g && g.text) || ''
})
function setGreeting(value) {
  const m = _ensureMode('greeting')
  m.text = value
}
function setModeHidden(key, hidden) {
  _ensureMode(key).hidden = hidden
  saveDoorman()
}

// Upsert an override into doorman_modes by key. Skill entries use *_override
// (null falls back to the platform default); custom entries carry their own
// button_label / intro_text.
function _ensureMode(key) {
  let m = form.doorman_modes.find(e => e.key === key)
  if (!m) { m = { key }; form.doorman_modes.push(m) }
  return m
}
function setModeLabel(key, value) {
  const m = _ensureMode(key)
  if (key.startsWith('skill:')) m.button_label_override = value
  else m.button_label = value
}
function setModeIntro(key, value) {
  const m = _ensureMode(key)
  if (key.startsWith('skill:')) m.intro_text_override = value
  else m.intro_text = value
}

// Preview = the NEW-session menu: visible skill button(s) + Education. Skill
// taps don't speak — they deduct emeralds and start the reading; only Education
// streams. Hidden buttons are filtered out (mirrors the live menu).
const previewModes = computed(() => editableModes.value
  .filter(m => !m.hidden)
  .map(m => ({
    key: m.key,
    label: m.labelOverride || m.defaultLabel,
    intro: m.introOverride || '',
    action: m.kind === 'skill'
      ? 'Deducts emeralds and begins the reading (Grounding) - nothing is spoken.'
      : '',
  })))

// Auto-save on change (blur), like Reading Sessions — no explicit Save button.
async function saveDoorman() {
  doormanError.value = false
  doormanSaving.value = true
  try {
    await updateReader({ doorman_modes: form.doorman_modes })
    if (store.reader) store.reader.doorman_modes = form.doorman_modes
    doormanStatus.value = 'Saved'
    setTimeout(() => { if (doormanStatus.value === 'Saved') doormanStatus.value = '' }, 1500)
  } catch (e) {
    doormanError.value = true
    doormanStatus.value = parseAxiosError(e)
  } finally {
    doormanSaving.value = false
  }
}

// Voice editor state (Phase 3'). Hydrated from store.reader.voice_sections;
// composes back into Reader.voice on save via the BE serializer.
const voiceSections = ref({
  tone: '', style: '', specialty: '', boundaries: '',
})

const privacyError = ref('')
const voiceStatus = ref('')
const voiceError = ref(false)

// Mysticism — reader-wide voice-intensity dial.
const MYSTICISM_OPTIONS = [
  { value: 'grounded', label: 'Grounded', hint: 'Plain, concrete language - minimal mystical framing.' },
  { value: 'mystical', label: 'Mystical', hint: 'Warm and evocative - a balance of grounded and magical.' },
  { value: 'cosmic', label: 'Really Mystical', hint: 'Fully esoteric - cosmic, archetypal, symbolic language.' },
]
const mysticism = ref('mystical')
const mysticismStatus = ref('')
const mysticismError = ref(false)
const mysticismIndex = computed(() => {
  const i = MYSTICISM_OPTIONS.findIndex(o => o.value === mysticism.value)
  return i < 0 ? 1 : i
})
async function onMysticismSlide(idx) {
  const opt = MYSTICISM_OPTIONS[Number(idx)]
  if (!opt || opt.value === mysticism.value) return
  const prev = mysticism.value
  mysticism.value = opt.value
  mysticismError.value = false
  mysticismStatus.value = 'Saving…'
  try {
    await updateMysticism(opt.value)
    if (store.reader) store.reader.mysticism = opt.value
    mysticismStatus.value = 'Saved'
    setTimeout(() => { if (mysticismStatus.value === 'Saved') mysticismStatus.value = '' }, 1500)
  } catch (e) {
    mysticism.value = prev
    mysticismError.value = true
    mysticismStatus.value = 'Could not save'
  }
}

function hydrate() {
  const r = store.reader || {}
  mysticism.value = r.mysticism || 'mystical'
  form.name = r.name || ''
  form.teaser = r.teaser || ''
  form.reader_description = r.reader_description || ''
  form.birth_date = r.birth_date || ''
  form.birth_time = r.birth_time || ''
  form.birth_location_name = r.birth_location_name || ''
  form.birth_latitude = r.birth_latitude ?? null
  form.birth_longitude = r.birth_longitude ?? null
  form.authored_by = r.authored_by || ''
  form.authored_by_website = r.authored_by_website || ''
  cardBack.reads_for = r.reads_for || ''
  cardBack.voice_short = r.voice_short || ''
  form.sub_domain = r.sub_domain || ''
  form.background_image = r.background_image || ''
  form.not_listed_in_community = !!r.not_listed_in_community
  voiceSections.value = coerceVoiceSections(r.voice_sections)
  // Doorman v1 — hydrate the editable per-mode menu entries. Deep-clone the
  // array so edits don't mutate the store until the operator hits Save.
  form.doorman_modes = Array.isArray(r.doorman_modes)
    ? JSON.parse(JSON.stringify(r.doorman_modes))
    : []
}

// Aida slide-in for Voice now happens on per-field FieldHelpIcon
// clicks inside VoiceEditor — no panel-level toggle needed.

async function onPrivacyToggle(checked) {
  privacyError.value = ''
  const previous = form.not_listed_in_community
  form.not_listed_in_community = checked
  try {
    await updateReader({ not_listed_in_community: checked })
    if (store.reader) store.reader.not_listed_in_community = checked
  } catch (e) {
    // Roll back the toggle on failure so the UI matches reality.
    form.not_listed_in_community = previous
    privacyError.value = parseAxiosError(e)
  }
}

// Per-section publish — VoiceEditor stages Aida's polish per
// section (tone/style/specialty/boundaries) and fires this when the
// operator clicks Publish on a Proposed card. We persist the whole
// voice_sections dict (BE recomposes Reader.voice automatically) and
// resolve the Promise the editor is awaiting so it can clear the
// draft + spinner.
async function onPublishVoiceSection({ section, value, resolve, reject }) {
  voiceStatus.value = ''
  voiceError.value = false
  try {
    const next = { ...voiceSections.value, [section]: value }
    voiceSections.value = next
    await updateReader({ voice_sections: next })
    if (store.reader) store.reader.voice_sections = next
    voiceStatus.value = 'Saved.'
    setTimeout(() => { voiceStatus.value = '' }, 3000)
    resolve()
  } catch (e) {
    voiceError.value = true
    voiceStatus.value = parseAxiosError(e)
    reject(e)
  }
}

onMounted(() => {
  hydrate()
  fetchDropImage()
  fetchChart()
  loading.value = false
  // Aida's polish output writes back to the form via these window
  // events:
  //   ``polish-applied`` — auto-write fields (name, etc.). We patch
  //     ``form`` and PATCH the BE so the public profile updates.
  //   ``field-staged`` — propose-then-publish fields (teaser,
  //     description). We stash the proposal in a draft ref and the
  //     operator hits Publish / Refine / Discard inline.
  window.addEventListener('studio-assistant:polish-applied', onPolishApplied)
  window.addEventListener('studio-assistant:field-staged', onFieldStaged)
  window.addEventListener('altar-image-updated', onSignImageUpdated)
  measureModalRight()
  window.addEventListener('resize', measureModalRight)
})
onUnmounted(() => {
  window.removeEventListener('studio-assistant:polish-applied', onPolishApplied)
  window.removeEventListener('studio-assistant:field-staged', onFieldStaged)
  window.removeEventListener('altar-image-updated', onSignImageUpdated)
  window.removeEventListener('resize', measureModalRight)
})
// Re-measure the scrim's left edge whenever a modal opens (the assistant pane
// may have shifted the content) — nextTick so layout has settled.
watch([() => view.value, textModalField], ([v, tf]) => {
  const open = v === 'avatar' || v === 'drop' || !!tf
  // Tell ReaderPage to show the Mystic AIda chat pane alongside the editor
  // (avatar/drop don't open the field assistant on their own).
  window.dispatchEvent(new CustomEvent('studio-editor-modal', { detail: { source: 'profile', open } }))
  if (open) {
    nextTick(measureModalRight)
    // Re-measure after the chat pane has rendered/settled so the modal hugs it.
    setTimeout(measureModalRight, 80)
    setTimeout(measureModalRight, 420)
  }
})

function onFieldStaged(e) {
  const { field, value } = e.detail || {}
  if (!field || value === undefined) return
  const slot = STAGE_DRAFTS[field]
  if (!slot) return
  slot.value = value
}

async function publishTeaser() {
  if (draftSaving.value || teaserDraft.value === null) return
  draftSaving.value = true
  try {
    const before = form.teaser
    form.teaser = teaserDraft.value
    if (await persist({ teaser: teaserDraft.value })) teaserDraft.value = null
    else form.teaser = before   // refused: the live text stays, the draft keeps their words
  } finally {
    draftSaving.value = false
  }
}
function discardTeaser() {
  if (draftSaving.value) return
  delete fieldErrors.teaser
  teaserDraft.value = null
}

async function publishDescription() {
  if (draftSaving.value || descriptionDraft.value === null) return
  draftSaving.value = true
  try {
    const before = form.reader_description
    form.reader_description = descriptionDraft.value
    if (await persist({ reader_description: descriptionDraft.value })) descriptionDraft.value = null
    else form.reader_description = before   // refused: the live text stays, the draft keeps their words
  } finally {
    draftSaving.value = false
  }
}
function discardDescription() {
  if (draftSaving.value) return
  delete fieldErrors.reader_description
  descriptionDraft.value = null
}

async function onPolishApplied(e) {
  const { field, value } = e.detail || {}
  if (!field || value === undefined) return
  // Doorman fields — keyed `doorman:<modeKey>:label|intro` (modeKey may itself
  // contain a colon, e.g. skill:tarot). Route into the right doorman_modes
  // override; saved with the rest on Save Doorman (no separate persist).
  if (field === 'doorman:greeting') {
    setGreeting(value)
    saveDoorman()
    return
  }
  if (field.startsWith('doorman:')) {
    const rest = field.slice('doorman:'.length)
    const at = rest.lastIndexOf(':')
    const modeKey = rest.slice(0, at)
    const part = rest.slice(at + 1)
    if (part === 'label') setModeLabel(modeKey, value)
    else if (part === 'intro') setModeIntro(modeKey, value)
    saveDoorman()  // assistant writes auto-save, like a blur
    return
  }
  if (!(field in form)) return  // not a profile field — ignore
  const before = { [field]: form[field], sub_domain: form.sub_domain }
  form[field] = value
  const patch = { [field]: value }
  // Renaming the reader also re-slugs the sub_domain so the public
  // URL displayed under the name on this card stays clickable.
  // If the new slug collides with another reader's, the BE 400s and
  // ``persist`` surfaces that error — operator can manually pick a
  // different sub_domain in Identity.
  if (field === 'name') {
    const newSlug = slugify(value)
    if (newSlug && newSlug !== form.sub_domain) {
      patch.sub_domain = newSlug
      form.sub_domain = newSlug
    }
  }
  if (!(await persist(patch))) Object.assign(form, before)
}

// Public text the server refused (Spec 2), keyed by field; shown under it.
const fieldErrors = reactive({})

// Saves the patch; true when it saved. A refusal of one public field puts
// its message under that field instead of the status line. Saving is
// all-or-nothing on the server, so callers put the old values back.
// The back of the room's card: READS FOR and VOICE (60 characters each).
const cardBack = reactive({ reads_for: '', voice_short: '' })
const cardBackSaving = ref(false)
async function saveCardBack() {
  if (cardBackSaving.value) return
  cardBackSaving.value = true
  const patch = { reads_for: cardBack.reads_for.trim(), voice_short: cardBack.voice_short.trim() }
  if (await persist(patch)) Object.assign(cardBack, patch)
  cardBackSaving.value = false
}

async function persist(patch) {
  saveStatus.value = 'Saving…'
  saveError.value = false
  try {
    const r = await updateReader(patch)
    if (r?.reader) Object.assign(store.reader, r.reader)
    Object.keys(patch).forEach((f) => { delete fieldErrors[f] })
    saveStatus.value = 'Saved'
    setTimeout(() => { if (saveStatus.value === 'Saved') saveStatus.value = '' }, 1800)
    return true
  } catch (e) {
    const refused = profileFieldRejection(e)
    if (refused) {
      fieldErrors[refused.field] = refused.message
      saveStatus.value = ''
    } else {
      saveError.value = true
      saveStatus.value = parseAxiosError(e) || 'Save failed'
    }
    return false
  }
}

function slugify(s) {
  return (s || '').toString().trim().toLowerCase()
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
}

// Subdomain shown under the reader name is derived from the live
// name so renames are reflected immediately. The stored sub_domain
// on the Reader is still what the public route uses; it gets a
// fresh slug saved alongside any name update so the link stays live.

// "Conjured By" defaults to the reader's own name when the operator
// hasn't credited a separate creator. authored_by_website (if set)
// turns this name into a hyperlink in the footer.
const effectiveAuthor = computed(
  () => (form.authored_by || '').trim() || form.name || 'Anonymous',
)

// Inline editor for the Conjured By credit. Two fields — author
// name + optional website — editable without opening the AI chat.
const editingAuthor = ref(false)
const authorSaving = ref(false)
const authorEdit = reactive({ name: '', website: '' })

function openAuthorEdit() {
  authorEdit.name = form.authored_by || ''
  authorEdit.website = form.authored_by_website || ''
  editingAuthor.value = true
}
function cancelAuthorEdit() {
  if (authorSaving.value) return
  delete fieldErrors.authored_by
  editingAuthor.value = false
}
async function saveAuthorEdit() {
  if (authorSaving.value) return
  authorSaving.value = true
  try {
    const patch = {
      authored_by: authorEdit.name.trim(),
      authored_by_website: authorEdit.website.trim(),
    }
    const before = { authored_by: form.authored_by, authored_by_website: form.authored_by_website }
    Object.assign(form, patch)
    if (await persist(patch)) editingAuthor.value = false
    else Object.assign(form, before)   // refused: the editor stays open with their words
  } finally {
    authorSaving.value = false
  }
}
</script>

<style scoped>
@import '@/styles/studio-forms.css';

.profile-panel {
  /* Profile card uses the same centered, capped layout as the rest
     of the studio panels — the public reader page is wider, but
     the Studio Profile tab is for editing, not viewing in full. */
}
/* Match Reading Sessions: opt out of the 640 centered cap so the panel fills
   the canvas, left-aligned, leaving room for the Doorman simulator rail. The
   editing columns stay capped for readability. */
.profile-wide.studio-panel { max-width: none; }
.profile-wide .profile-card,
.profile-wide .voice-card { max-width: 640px; }
/* The hero is padding-less, but .profile-card adds 28px padding + 1px border
   each side (content-box), making its border box ~698px wide. Match that so the
   right-aligned signs land flush with the card's right edge below. */
.profile-wide .profile-hero { max-width: 698px; padding: 0 28px; box-sizing: border-box; }
/* Profile sub-tabs — mirrors the Reading Sessions sub-tab strip. */
.profile-subtabs {
  display: flex;
  gap: 2px;
  flex-wrap: wrap;
  margin-bottom: 24px;
  border-bottom: 1px dashed rgba(255, 255, 255, 0.08);
}
.profile-subtab {
  background: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  color: rgba(195, 169, 255, 0.55);
  font-family: 'Cinzel', serif;
  font-size: 13px;
  letter-spacing: 0.4px;
  padding: 8px 16px;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;
}
.profile-subtab:hover { color: rgba(232, 216, 255, 0.9); }
.profile-subtab.active { color: #C9A94E; border-bottom-color: #C9A94E; }
.profile-heading {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 10px;
}
/* Section descriptor used in place of a title (the tab already names it). */
.profile-section-desc {
  margin: 0 0 18px;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-style: italic;
  font-weight: 500;
  font-size: 18px;
  color: rgba(212, 196, 232, 0.6);
}
.profile-heading-sub {
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-style: italic;
  font-size: 14px;
  color: rgba(212, 196, 232, 0.55);
  font-weight: 400;
  letter-spacing: 0;
  text-transform: none;
}
.profile-loading {
  text-align: center;
  color: rgba(212, 196, 232, 0.6);
  padding: 36px 0;
  font-style: italic;
}

.profile-card {
  margin-top: 12px;
  padding: 28px 28px 24px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(195, 169, 255, 0.16);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: 22px;
}

/* Avatar */
/* Two-column identity layout (mirrors the public reader card). */
.identity-grid {
  display: flex;
  gap: 26px;
  align-items: flex-start;
  flex-wrap: wrap;
}
.identity-left {
  flex: 0 0 240px;
  max-width: 240px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
}
.identity-right { flex: 1 1 320px; min-width: 280px; }
.identity-name-block { text-align: left; }
/* Name and its pencil, left-aligned in the meta column, the name allowed to
   shrink (min-width: 0) so it stays on one line. */
.identity-name-row { display: flex; align-items: center; justify-content: flex-start; gap: 8px; min-width: 0; }
.identity-name-row .profile-name { margin: 0; }
/* Sun + Rising sign cards (mirrors the Altar Mirror widget). */
/* Hero — avatar + name + cake (left), sun/rising cards (top-right). */
/* Stretch so the meta column fills the avatar's height — lets the cake pin to
   the bottom-left while the name takes the top. Meta is content-sized (NOT
   flex:1) so the signs column keeps its room on the right. */
.profile-hero { display: flex; align-items: stretch; gap: 18px; margin-bottom: 14px; position: relative; }
/* Name, signs, birth: one column beside the avatar. */
.profile-hero-meta {
  display: flex; flex-direction: column; gap: 10px; min-width: 0; flex: 1;
}
/* The birth row — cake (a fact, or an editor for legacy readers) + the chart link. */
.profile-born-row { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; }
.profile-born {
  display: inline-flex; align-items: center; gap: 8px;
  background: none; border: none; padding: 0; cursor: pointer; color: rgba(195,169,255,0.7);
}
.profile-born.fixed { cursor: default; }
.profile-born.fixed .profile-born-label { color: rgba(195,169,255,0.7); font-size: 14px; }
.profile-born:hover { color: #e9dcff; }
.profile-born-label { font-size: 13px; letter-spacing: 0.4px; color: rgba(195,169,255,0.6); }
.profile-born-edit { color: #9b86c9; font-size: 12px; opacity: 0; transition: opacity 0.15s, color 0.15s; }
.profile-born:hover .profile-born-edit { opacity: 1; color: #f0d9a3; }
.profile-read-chart {
  background: none; border: none; padding: 0; cursor: pointer;
  font-family: 'Cinzel', serif; font-size: 12px; letter-spacing: 1.5px; text-transform: uppercase;
  color: #c9a94e;
}
.profile-read-chart:hover { color: #ffdfa3; }
/* The chart sheet, as a modal. */
.chart-modal {
  position: fixed; inset: 0; z-index: 60; display: flex; align-items: flex-start; justify-content: center;
  padding: 40px 16px; overflow-y: auto; background: rgba(5, 0, 15, 0.82);
}
.chart-modal-card {
  position: relative; width: 100%; max-width: 640px; padding: 26px 24px 30px; border-radius: 14px;
  background: #0d0520; border: 1px solid rgba(201,169,78,0.25);
}
.chart-modal-close {
  position: absolute; top: 10px; right: 12px; background: none; border: none; cursor: pointer;
  font-size: 22px; line-height: 1; color: rgba(195,169,255,0.6);
}
.chart-modal-close:hover { color: #ffdfa3; }
.chart-modal-wait { margin: 0; font-size: 14px; color: rgba(195,169,255,0.55); }
/* Sign cards — below the name, in the meta column. */
/* Sun + Moon + Rising — square image tiles with the name/role overlaid on a dark
   gradient, mirroring the public reader card (.reader-sign-card). Two tiles wide:
   a single stacked column would run to 340px with the Moon added and become the
   tallest thing in the hero, so the third tile wraps under the first two and the
   block stays about as tall as the avatar beside it. `max-width` lets it fall back
   to one tile per row when the panel is narrow. */
.identity-signs {
  display: flex; flex-wrap: wrap; gap: 8px;
}
.identity-sign-card {
  /* Three tiles fill the meta column, edge to edge with the name above and
     the date below — the same width the drop image spans in the card. */
  flex: 1 1 0; min-width: 88px; max-width: 112px; position: relative;
  aspect-ratio: 1 / 1; border-radius: 12px; overflow: hidden;
  border: 1px solid rgba(155,134,201,0.25); background: rgba(255,255,255,0.03);
}
.identity-sign-img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
.identity-sign-glyph {
  position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
  font-size: 36px; color: #f0e6ff;
  background: linear-gradient(180deg, rgba(138,95,214,0.45), rgba(102,51,153,0.4));
}
.identity-sign-text {
  position: absolute; left: 0; right: 0; bottom: 0; z-index: 1;
  display: flex; flex-direction: column; align-items: center;
  padding: 12px 6px 6px;
  background: linear-gradient(to top, rgba(6,2,14,0.94), rgba(6,2,14,0.45) 60%, transparent);
}
.identity-sign-name { font-family: 'Cinzel', serif; font-size: 14px; color: #f5e8c0; line-height: 1.1; }
.identity-sign-role { font-size: 9px; letter-spacing: 0.12em; text-transform: uppercase; color: rgba(245,232,192,0.8); }
/* Universal edit pencil — the Altar Mirror's minimal violet fa-pencil, used app-
   wide. Icon-only, no backing: violet at rest, gold on hover. Position is set per
   placement by the element's own class. */
.mystic-pencil {
  background: none; border: none; padding: 2px; cursor: pointer; line-height: 1;
  color: #9b86c9; font-size: 14px; transition: color 0.2s, transform 0.1s;
}
.mystic-pencil:hover { color: #f0d9a3; transform: scale(1.08); }
.mystic-pencil:active { transform: scale(0.94); }

/* Sign-card pencil — position only; the look comes from .mystic-pencil. */
.identity-sign-edit {
  position: absolute; top: 6px; right: 8px; z-index: 2; margin: 0;
  color: #f0e6ff; text-shadow: 0 1px 4px rgba(0,0,0,0.85);
}

/* Daily-drop hero — landscape image with the affirmation caption overlaid. */
.identity-drop { margin-bottom: 18px; }
/* Drop eyebrow — name + date above the image, matching the community card. */
.identity-drop-over {
  display: flex; align-items: center; gap: 8px; margin-bottom: 8px;
}
.identity-drop-over-name {
  font-family: 'Cinzel', serif; font-size: 11px; letter-spacing: 0.16em;
  text-transform: uppercase; color: #C9A94E;
}
.identity-drop-over-dot { color: rgba(195,169,255,0.5); }
.identity-drop-over-date {
  font-family: 'Cinzel', serif; font-size: 12px; font-style: italic;
  color: rgba(195,169,255,0.75);
}
.aff-over {
  display: flex; align-items: center; gap: 8px; margin-bottom: 8px;
  font-family: 'Cinzel', serif; font-size: 11px; letter-spacing: 0.16em;
  text-transform: uppercase; color: #C9A94E;
}
.aff-over-date { color: rgba(195,169,255,0.7); font-style: italic; text-transform: none; letter-spacing: 0.02em; }
.aff-over-dot { color: rgba(195,169,255,0.5); }
.identity-drop-hero {
  position: relative; border-radius: 16px; overflow: hidden;
  border: 1px solid rgba(201,169,78,0.3); background: rgba(0,0,0,0.4);
  aspect-ratio: 3 / 2;
}
.identity-drop-img { width: 100%; height: 100%; object-fit: cover; display: block; }
.identity-drop-noimg {
  width: 100%; height: 100%; display: flex; align-items: center; justify-content: center;
  font-size: 40px; color: rgba(201,169,78,0.45);
}
/* Caption strip — the affirmation over a dark gradient at the bottom of the image. */
.identity-drop-footer {
  position: absolute; left: 0; right: 0; bottom: 0; padding: 30px 16px 14px;
  background: linear-gradient(to top, rgba(8,4,18,0.92), rgba(8,4,18,0.35) 70%, transparent);
}
.identity-drop-caption {
  margin: 0; text-align: center;
  font-family: 'Cinzel', serif; font-style: italic;
  font-size: 15px; line-height: 1.4; color: #f5e8c0;
}
/* Drop pencil — universal violet pencil, top-right of the image. A strong dark
   halo keeps it legible whether the corner is a bright sky or dark scene. */
.identity-drop-edit {
  position: absolute; top: 10px; right: 10px; font-size: 16px;
  filter: drop-shadow(0 0 4px rgba(0,0,0,0.95)) drop-shadow(0 1px 2px rgba(0,0,0,0.9));
}

/* Back link for the drop takeover — mirrors AvatarPicker's back button. */
.drop-back-btn {
  background: transparent;
  border: 1px solid rgba(201, 169, 78, 0.4);
  color: rgba(232, 216, 255, 0.85);
  font-family: 'Cinzel', serif;
  font-size: 13px;
  border-radius: 8px;
  padding: 7px 14px;
  margin-bottom: 16px;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
}
.drop-back-btn:hover { border-color: #C9A94E; color: #C9A94E; }
.profile-avatar-wrap {
  position: relative;
  flex: 0 0 auto;
  align-self: flex-start;  /* the name's top line sits level with the image's top edge */
  width: 172px;
  height: 172px;         /* SQUARE — Readers are square (Seekers are round). */
  border-radius: 16px;
  border: 2px solid rgba(201, 169, 78, 0.55);
  box-shadow: 0 0 28px rgba(201, 169, 78, 0.18);
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
}
.profile-avatar {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  border-radius: 14px;
}
.profile-avatar-placeholder {
  font-size: 48px;
  color: rgba(201, 169, 78, 0.55);
}
/* Avatar pencil — universal violet pencil, overhanging the avatar's bottom-right
   corner onto the dark panel so it reads clearly. Look comes from .mystic-pencil. */
.avatar-pencil {
  position: absolute; right: -4px; bottom: -4px;
  font-size: 15px;
  filter: drop-shadow(0 1px 3px rgba(0,0,0,0.7));
}

/* Block layout */
.profile-block {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.block-head {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}
.block-label {
  font-family: 'Cinzel', serif;
  font-size: 11px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(212, 196, 232, 0.55);
  margin-right: auto;
}
.profile-name {
  font-family: 'Cinzel', serif;
  color: #FFDFA3;
  /* One line, always: the signs and the birth sit under it and want a
     steady top. A long name shrinks before it wraps. */
  font-size: clamp(18px, 2.2vw, 26px);
  margin: 0;
  text-align: left;
  line-height: 1.15;
  font-weight: 600;
}
.profile-name.one-line { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%; min-width: 0; }
.profile-subdomain-link {
  display: block;
  margin: 4px 0 0;
  font-family: 'Cinzel', serif;
  font-size: 12px;
  letter-spacing: 0.06em;
  color: #C9A94E;
  text-align: center;
  text-decoration: none;
  font-style: italic;
  opacity: 0.85;
}
.profile-subdomain-link:hover {
  text-decoration: underline;
  opacity: 1;
}
.field-err {
  margin: 6px 0 0;
  font-size: 0.85rem;
  line-height: 1.4;
  color: var(--danger, #c0392b);
}

.profile-teaser {
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-style: italic;
  font-size: 16px;
  color: rgba(232, 216, 255, 0.92);
  margin: 0;
  text-align: center;
  line-height: 1.5;
}
.profile-description {
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-size: 15px;
  color: rgba(232, 216, 255, 0.82);
  margin: 0;
  line-height: 1.65;
  text-align: center;
}
.profile-empty {
  font-size: 13px;
  color: rgba(212, 196, 232, 0.4);
  margin: 0;
  text-align: center;
  font-style: italic;
}

/* Staged-draft card — Aida's polished proposal sits below the
   live field with Publish / Refine / Discard. Gold accent flags
   that there's a pending change for the operator to review. */
/* Text-field edit modal body (name / teaser / description). */
.text-modal-body { display: flex; flex-direction: column; gap: 12px; }
.text-modal-current {
  margin: 0; font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic;
  font-size: 16px; line-height: 1.5; color: #f5e8c0; text-align: center;
}
.text-modal-empty { margin: 0; font-size: 14px; color: rgba(195,169,255,0.5); font-style: italic; text-align: center; }
.text-modal-hint { margin: 2px 0 0; font-size: 12.5px; color: rgba(195,169,255,0.6); font-style: italic; line-height: 1.5; text-align: center; }

.draft-card {
  margin-top: 10px;
  padding: 12px 14px;
  background: rgba(201, 169, 78, 0.08);
  border: 1px solid rgba(201, 169, 78, 0.5);
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.draft-label {
  font-family: 'Cinzel', serif;
  font-size: 10px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #C9A94E;
}
.draft-text {
  margin: 0;
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-style: italic;
  font-size: 15px;
  line-height: 1.55;
  color: rgba(232, 216, 255, 0.95);
}
.draft-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}
.ghost-btn-sm,
.primary-btn-sm {
  padding: 6px 14px;
  border-radius: 8px;
  font-family: 'Cinzel', serif;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  cursor: pointer;
}
.ghost-btn-sm {
  background: transparent;
  border: 1px solid rgba(195, 169, 255, 0.32);
  color: rgba(212, 196, 232, 0.85);
}
.ghost-btn-sm:hover:not(:disabled) {
  border-color: #C9A94E;
  color: #C9A94E;
}
.primary-btn-sm {
  background: rgba(201, 169, 78, 0.95);
  border: 1px solid rgba(201, 169, 78, 0.95);
  color: #1a0c2e;
}
.primary-btn-sm:hover:not(:disabled) { filter: brightness(1.06); }
.ghost-btn-sm:disabled,
.primary-btn-sm:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Footer */
.card-back-edit { display: flex; flex-direction: column; gap: 8px; margin: 18px 0 6px; }
.card-back-head { margin: 0; font-family: 'Cinzel', serif; font-size: 12px; letter-spacing: 0.16em; text-transform: uppercase; color: #C9A94E; }
.profile-footer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding-top: 8px;
  border-top: 1px solid rgba(195, 169, 255, 0.12);
}
.footer-by {
  font-size: 12px;
  color: rgba(212, 196, 232, 0.7);
  font-style: italic;
  letter-spacing: 0.04em;
}
.footer-by .author-link {
  color: #C9A94E;
  text-decoration: none;
  font-style: italic;
  margin-left: 4px;
}
.footer-by .author-link:hover { text-decoration: underline; }

.author-edit-btn {
  margin-left: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  padding: 0;
  background: rgba(201, 169, 78, 0.12);
  border: 1px solid rgba(201, 169, 78, 0.4);
  border-radius: 50%;
  color: #C9A94E;
  font-family: 'Cinzel', serif;
  font-size: 12px;
  line-height: 1;
  cursor: pointer;
  vertical-align: middle;
  transition: all 0.15s;
}
.author-edit-btn:hover {
  background: rgba(201, 169, 78, 0.22);
  border-color: #C9A94E;
  transform: scale(1.06);
}

.author-edit-form {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  max-width: 380px;
  padding: 12px 14px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(195, 169, 255, 0.18);
  border-radius: 10px;
}
.author-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: left;
}
.author-field > span {
  font-family: 'Cinzel', serif;
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(212, 196, 232, 0.6);
}
.author-field > small {
  font-size: 11px;
  color: rgba(212, 196, 232, 0.45);
  font-style: italic;
}
.author-input {
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(195, 169, 255, 0.18);
  border-radius: 6px;
  padding: 8px 10px;
  color: #f3e9d2;
  font-family: inherit;
  font-size: 13px;
  outline: none;
  transition: border-color 0.18s;
}
.author-input:focus { border-color: #C9A94E; }
.author-edit-actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  margin-top: 4px;
}
.author-edit-actions .ghost-btn {
  background: transparent;
  border: 1px solid rgba(195, 169, 255, 0.32);
  color: rgba(212, 196, 232, 0.85);
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 12px;
  font-family: inherit;
  cursor: pointer;
}
.author-edit-actions .ghost-btn:hover:not(:disabled) {
  border-color: #C9A94E;
  color: #C9A94E;
}
.author-edit-actions .primary-btn {
  background: rgba(201, 169, 78, 0.95);
  border: 1px solid rgba(201, 169, 78, 0.95);
  color: #1a0c2e;
  padding: 6px 16px;
  border-radius: 8px;
  font-size: 12px;
  font-family: 'Cinzel', serif;
  font-weight: 600;
  cursor: pointer;
}
.author-edit-actions .primary-btn:hover:not(:disabled) {
  filter: brightness(1.06);
}
.author-edit-actions button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.save-status {
  margin: 8px 0 0;
  text-align: center;
  font-size: 12px;
  color: rgba(125, 232, 204, 0.85);
  font-style: italic;
}
.save-status.error {
  color: rgba(244, 67, 54, 0.9);
}

/* ─── Privacy row (lives inside the Profile card) ────────────── */
.profile-privacy-row {
  margin-top: 24px;
  padding-top: 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}
.profile-privacy-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: rgba(212, 196, 232, 0.85);
  cursor: pointer;
}
.profile-privacy-label input[type='checkbox'] {
  width: 16px;
  height: 16px;
  cursor: pointer;
}
.profile-privacy-hint {
  font-size: 12px;
  color: rgba(195, 169, 255, 0.5);
  font-style: italic;
}

/* ─── Voice card (peer to Profile) ───────────────────────────── */
/* Mirrors .profile-card so the two surfaces read as twins — same
   bordered + elevated box treatment, same gold-purple palette. */
.voice-card {
  margin-top: 32px;
}
.voice-card-body {
  margin-top: 12px;
  padding: 28px 28px 24px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(195, 169, 255, 0.16);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.voice-card-intro {
  margin: 0 0 4px;
  font-size: 13px;
  color: rgba(195, 169, 255, 0.65);
  font-style: italic;
}
.voice-actions {
  margin-top: 14px;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

/* ─── Doorman card (peer to Voice) ─────────────────────────────
   Mirrors .voice-card / .profile-card so the three surfaces read
   as a coherent set — same bordered + elevated box treatment, same
   gold-purple palette. Doorman authoring is the operator-facing
   surface for the new every-session entry state. */
.doorman-card {
  margin-top: 32px;
}
.doorman-card-body {
  margin-top: 12px;
  padding: 28px 28px 24px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(195, 169, 255, 0.16);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.doorman-mode-row {
  padding: 14px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(195, 169, 255, 0.10);
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.doorman-mode-header {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.doorman-show-toggle {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(195, 169, 255, 0.6);
  cursor: pointer;
}
.doorman-show-toggle input { accent-color: #C9A94E; cursor: pointer; }
.doorman-mode-hidden { opacity: 0.55; }
.doorman-greeting-field { margin-bottom: 4px; }
.doorman-mode-key {
  font-family: 'SFMono-Regular', Menlo, monospace;
  font-size: 12px;
  color: #c3a9ff;
  background: rgba(195, 169, 255, 0.10);
  padding: 2px 8px;
  border-radius: 4px;
}
.doorman-mode-auto-label {
  font-size: 11px;
  color: rgba(195, 169, 255, 0.55);
  font-style: italic;
}
.doorman-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.doorman-field-label {
  font-size: 11px;
  color: rgba(195, 169, 255, 0.65);
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.doorman-input {
  width: 100%;
  box-sizing: border-box;
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(195, 169, 255, 0.18);
  border-radius: 6px;
  padding: 8px 10px;
  color: #fff;
  font-size: 13px;
  font-family: inherit;
}
.doorman-input:focus {
  outline: none;
  border-color: rgba(195, 169, 255, 0.4);
}
.doorman-textarea {
  resize: vertical;
  min-height: 48px;
}
.doorman-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
/* Doorman simulator — mirrors the Reading Sessions split exactly. Closed: a
   single 600px (capped at 100%) form column, left-aligned. Open: a grid whose
   center cell shrinks (minmax(0,1fr)) so the form gives way when the assistant
   panel squeezes the canvas, while the rail holds — never wraps/breaks. */
.doorman-split { display: block; }
.doorman-split-open {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(240px, 320px);
  gap: 1.25rem;
  align-items: start;
}
.doorman-col { width: 600px; max-width: 100%; min-width: 0; }
.doorman-rail { position: sticky; top: 0; min-width: 0; }
@media (max-width: 900px) {
  .doorman-split-open { grid-template-columns: 1fr; }
  .doorman-rail { position: static; }
}
/* ▶ Simulate — matches the beat Simulate button on Reading Sessions. */
.doorman-sim-btn {
  margin-left: auto;
  background: rgba(102, 51, 153, 0.28);
  border: 1px solid rgba(160, 120, 220, 0.5);
  color: #E8DDFF;
  border-radius: 999px;
  padding: 9px 18px;
  font-size: 13px;
  font-family: 'Cinzel', serif;
  cursor: pointer;
  transition: all 0.15s;
}
.doorman-sim-btn:hover { background: rgba(102, 51, 153, 0.42); border-color: rgba(160, 120, 220, 0.8); }
/* Mysticism slider — nested within the Voice card. */
.mysticism-block {
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid rgba(195, 169, 255, 0.14);
}
.mysticism-subhead {
  margin: 0 0 10px;
  font-family: 'Cinzel', serif;
  font-size: 15px;
  color: #C9A94E;
}
.mysticism-subhead-note {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-style: italic;
  color: rgba(195, 169, 255, 0.6);
  margin-left: 4px;
}
.mysticism-slider { width: 100%; accent-color: #C9A94E; cursor: pointer; }
.mysticism-ticks {
  display: flex;
  justify-content: space-between;
  margin-top: 6px;
}
.mysticism-tick {
  font-size: 12px;
  color: rgba(212, 196, 232, 0.55);
  letter-spacing: 0.02em;
}
.mysticism-tick.active { color: #C9A94E; font-weight: 600; }
.mysticism-hint {
  margin: 8px 0 0;
  font-size: 12px;
  font-style: italic;
  color: rgba(195, 169, 255, 0.6);
}

/* ── Phone (≤560px): stack the two-column hero, shrink the fixed avatar to fit,
   drop the fixed 600px doorman column to full-width, and enlarge the tiny edit
   pencils to a 44px touch target. Desktop layout above is untouched. ── */
@media (max-width: 560px) {
  .profile-hero { flex-direction: column; align-items: center; text-align: center; }
  .profile-avatar-wrap {
    align-self: center;
    width: clamp(96px, 40vw, 140px);
    height: auto;
    aspect-ratio: 1;
  }
  .doorman-col { width: 100%; max-width: 100%; }
  .mystic-pencil { padding: 10px; min-width: 44px; min-height: 44px; }
}
</style>
